import React, { useEffect, useState } from "react";
import { dateTimeLong } from "@/lib/dates";
import { api, TIMEOUTS } from "@/lib/api";
import { Mail, Send, AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ATENA_MODELS } from "@/lib/atenaModels";

const FREQUENCIES = [
  ["daily", "Zilnic"],
  ["weekly", "Săptămânal (luni)"],
  ["manual", "Doar la cerere"],
];
const TIMEZONES = [
  "Europe/Bucharest", "Europe/London", "Europe/Berlin", "Europe/Madrid",
  "America/New_York", "America/Los_Angeles", "UTC",
];

export default function DigestSettings({ workspaceId }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/digest/settings", { params: { workspace_id: workspaceId } })
      .then((r) => { setCfg(r.data); setEmails((r.data.emails || []).join(", ")); })
      .catch(() => setCfg(null));
  }, [workspaceId]);

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/digest/settings", payload,
        { params: { workspace_id: workspaceId } });
      setCfg(data);
      toast.success("Setări digest salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    setPreview("");
    try {
      const { data } = await api.post("/digest/send", null,
        { params: { workspace_id: workspaceId }, timeout: TIMEOUTS.digestSend });
      setPreview(data.digest || "");
      if (data.error) toast.error(data.error);
      else toast.success(`Digest trimis către ${data.sent}/${data.recipients} destinatari`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trimiterea a eșuat");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="digest-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Mail className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Email cu rezumat</h3>
        <span className="label-tiny ml-2">// VIA ATENA</span>
        <span
          data-testid="digest-email-status"
          className={`ml-auto flex items-center gap-1.5 text-xs ${cfg.email_configured ? "text-brand" : "text-fg-muted"}`}
        >
          {cfg.email_configured
            ? <><Check className="w-3.5 h-3.5" /> email configurat</>
            : <><AlertTriangle className="w-3.5 h-3.5" /> RESEND_API_KEY lipsește</>}
        </span>
      </div>

      <div className="p-5 space-y-4">
        {!cfg.email_configured && (
          <p className="text-xs text-fg-muted leading-relaxed">
            Fără <span className="mono">RESEND_API_KEY</span> în environment digest-ul se generează,
            dar nu pleacă pe email — „Trimite acum" îți arată textul aici.
          </p>
        )}

        <div className="flex items-start gap-3">
          <button
            type="button" role="switch" aria-checked={cfg.enabled} aria-label="Digest activ"
            data-testid="digest-toggle" onClick={() => patch({ enabled: !cfg.enabled })}
            disabled={busy}
            className={`mt-0.5 flex-shrink-0 w-10 h-5 border transition-colors ${
              cfg.enabled ? "bg-brand/30 border-brand/50" : "bg-surface-2 border-line/20"}`}
          >
            <span className={`block w-4 h-4 mt-px transition-transform ${
              cfg.enabled ? "translate-x-5 bg-brand" : "translate-x-px bg-fg/40"}`} />
          </button>
          <div>
            <p className="text-sm font-medium">Digest programat</p>
            <p className="text-xs text-fg-muted">Atena rezumă tichetele, serverele și mențiunile din perioadă.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="digest-frequency" className="label-tiny block mb-1.5">Frecvență</label>
            <select
              id="digest-frequency" data-testid="digest-frequency" value={cfg.frequency}
              onChange={(e) => patch({ frequency: e.target.value })} disabled={busy}
              className="input-flat text-xs w-full"
            >{FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div>
            <label htmlFor="digest-hour" className="label-tiny block mb-1.5">Ora</label>
            <select
              id="digest-hour" data-testid="digest-hour" value={cfg.hour}
              onChange={(e) => patch({ hour: Number(e.target.value) })} disabled={busy}
              className="input-flat text-xs w-full"
            >{Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>))}</select>
          </div>
          <div>
            <label htmlFor="digest-tz" className="label-tiny block mb-1.5">Fus orar</label>
            <select
              id="digest-tz" data-testid="digest-timezone" value={cfg.timezone}
              onChange={(e) => patch({ timezone: e.target.value })} disabled={busy}
              className="input-flat text-xs w-full"
            >{TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          </div>
        </div>

        <div>
          <label htmlFor="digest-model" className="label-tiny block mb-1.5">Model digest</label>
          <select
            id="digest-model" data-testid="digest-model" value={cfg.model}
            onChange={(e) => patch({ model: e.target.value })} disabled={busy}
            className="input-flat text-xs w-full sm:w-72"
          >{ATENA_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select>
          <p className="text-[11px] text-fg-muted mt-1 leading-relaxed">
            Digest-ul e un singur apel pe zi al cărui rezultat e text citit de oameni, deci
            merită un model bun. Cifrele din digest se randează din baza de date, nu de model —
            aici se decide doar calitatea frazei de sinteză.
          </p>
        </div>

        <div>
          <label htmlFor="digest-recipients" className="label-tiny block mb-1.5">Destinatari</label>
          <select
            id="digest-recipients" data-testid="digest-recipients" value={cfg.recipients}
            onChange={(e) => patch({ recipients: e.target.value })} disabled={busy}
            className="input-flat text-xs w-full sm:w-64"
          >
            <option value="admins">Toți ownerii și adminii</option>
            <option value="custom">Listă proprie</option>
          </select>
          {cfg.recipients === "custom" && (
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                data-testid="digest-emails" value={emails} onChange={(e) => setEmails(e.target.value)}
                placeholder="a@firma.ro, b@firma.ro"
                className="input-flat text-xs mono flex-1 min-w-[220px]"
              />
              <button
                type="button" onClick={() => patch({ emails: emails.split(",").map((e) => e.trim()) })}
                disabled={busy} className="btn-ghost text-xs px-3"
              >Salvează lista</button>
            </div>
          )}
        </div>

        <button
          type="button" data-testid="digest-send-now" onClick={sendNow} disabled={busy}
          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Trimite digest acum
        </button>

        {cfg.last_sent_at && (
          <p className="text-[11px] text-fg-muted mono">
            Ultimul digest: {dateTimeLong(cfg.last_sent_at)}
          </p>
        )}

        {preview && (
          <div className="border border-line/10 bg-surface-2 p-3" data-testid="digest-preview">
            <p className="label-tiny mb-1.5">// TEXT GENERAT</p>
            <p className="text-xs whitespace-pre-wrap text-fg/80">{preview}</p>
          </div>
        )}
      </div>
    </section>
  );
}
